
import { SWRConfiguration, SWRResponse } from "swr";

import { AppSettings } from "@/AppSettings";
import { GetCaseReportFormState, GetCaseReportFormStateResponse, SourceDataEventContext, SourceDataEventState, SourceDataFormContext, SourceDataFormState, SourceDataItemState, SourceDataPoint, UpdatedSourceDataReviewEvent, UpdatedSourceDataVerificationEvent } from "@/api/dtos";
import { useMemo } from "react";
import useGet from "../api/useGet";

const ensureSourceDataPoint = (state: GetCaseReportFormStateResponse['events'], data: UpdatedSourceDataReviewEvent | UpdatedSourceDataVerificationEvent) => {
    
    if (!state) {
        state = {}
    };

    if (!state[data.studyEventOid!]) {
        state[data.studyEventOid!] = {};
    }
    
    if (!state[data.studyEventOid!][data.studyEventRepeat!]) {
        state[data.studyEventOid!][data.studyEventRepeat!] = new SourceDataEventState({
            event: new SourceDataEventContext({
                id: undefined,
                trialId: data.trialId,
                masterGroupId: data.masterGroupId,
                coordinatingGroupId: data.coordinatingGroupId,
                institutionId: data.institutionId,
                participantId: data.participantId,
                studyEventOid: data.studyEventOid,
                studyEventRepeat: data.studyEventRepeat
            }),
            forms: {}
        });
    }

    if (!state[data.studyEventOid!][data.studyEventRepeat!].forms) {
        state[data.studyEventOid!][data.studyEventRepeat!].forms = {};
    }

    if (!state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!]) {
        state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!] = new SourceDataFormState({
            form: new SourceDataFormContext({
                id: undefined,
                sourceDataEventContextId: undefined,
                caseReportFormOid: data.caseReportFormOid,
                caseReportFormVersionOid: data.caseReportFormVersionOid
            }),
            items: {}
        })
    }

    if (!state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items) {
        state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items = {};
    }

    const itemGroupNameKey = data.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey;

    if (!state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items![itemGroupNameKey]) {
        state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items![itemGroupNameKey] = {};
    }

    if (!state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items![itemGroupNameKey][data.itemGroupRepeat!]) {
        state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items![itemGroupNameKey][data.itemGroupRepeat!] = {};
    }

    if (!state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items![itemGroupNameKey][data.itemGroupRepeat!][data.itemName!]) {
        state[data.studyEventOid!][data.studyEventRepeat!].forms![data.caseReportFormOid!].items![itemGroupNameKey][data.itemGroupRepeat!][data.itemName!] = new SourceDataItemState({
            point: new SourceDataPoint({
                id: undefined,
                sourceDataFormContextId: undefined,
                itemGroupName: data.itemGroupName,
                itemGroupRepeat: data.itemGroupRepeat,
                itemName: data.itemName,
                priority: undefined,
                value: data.value,
            })
        });
    }

    return state;
}

const getData = (response?: SWRResponse<GetCaseReportFormStateResponse, GetCaseReportFormStateResponse>) => response?.data?.events;
const getError = (response?: SWRResponse<GetCaseReportFormStateResponse, GetCaseReportFormStateResponse>) => (response?.error ?? response?.data)?.responseStatus;
const getAdditionalState = (response?: SWRResponse<GetCaseReportFormStateResponse, GetCaseReportFormStateResponse>) => ({});

export const useCaseReportFormState = (
    trialId?: number, 
    masterGroupId?: number,
    coordinatingGroupId?: number,
    institutionId?: number,
    participantId?: number,
    studyEventOid?: string,
    studyEventRepeat?: number,
    caseReportFormOid?: string,
    configuration?: SWRConfiguration | undefined
) => {

    const request = useMemo(() => {
        if (!participantId /*|| !studyEventOid || !studyEventRepeat || !caseReportFormOid*/) {
            return undefined;
        }

        return () => {
            return new GetCaseReportFormState({ 
                participantId, studyEventOid, studyEventRepeat, caseReportFormOid 
            });
        }
    }, [trialId, masterGroupId, coordinatingGroupId, institutionId, participantId, studyEventOid, studyEventRepeat, caseReportFormOid, configuration]);

    const context =  useGet(request, { getData, getError, getAdditionalState }, configuration);

    return context;
}

export default useCaseReportFormState;